import { TablePaginationConfig } from 'antd';
import { FilterValue } from 'antd/lib/table/interface';
import { useTableSearchFilter } from 'hooks';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAPIAuditReports } from 'services/api/Endorsers';
import { AuditReportItem, ListAuditReportsOptionalParams } from 'services/api/client/src';
import useTabStore from './useTabStore';

const useAuditReportsFilters = (initParams?: ListAuditReportsOptionalParams) => {
  const search = useTabStore((state) => state.auditSearch);
  const [auditReportsParams, setAuditReportsParams] = useState<ListAuditReportsOptionalParams>({
    pageNumber: 1,
    pageSize: 50,
    ...initParams,
  });

  const { data: auditReportsResponse, isLoading: isAuditReportsLoading } =
    useAPIAuditReports(auditReportsParams);

  const totalItems = useMemo(() => auditReportsResponse?.totalItems || 0, [auditReportsResponse]);
  const auditReports: AuditReportItem[] = useMemo(
    () =>
      auditReportsResponse?.results?.map((item) => ({
        ...item,
        id: item.reportId,
      })) || [],
    [auditReportsResponse],
  );

  useEffect(() => {
    setAuditReportsParams({
      ...auditReportsParams,
      bapNumber: initParams?.bapNumber,
      facilityName: search?.facilityName,
      submittedDate:
        search.date !== undefined ? moment(search.date).format('yyyy-MM-DDTHH:mm:ss') : undefined,
      status: search.status !== '' ? [search.status || ''] : undefined,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, initParams?.bapNumber]);

  const { filters: facilityNameSearchFilter } = useTableSearchFilter({
    title: 'Facility Name',
  });

  const { filters: bapNumberSearchFilter } = useTableSearchFilter({
    title: 'BAP Number',
  });

  const onAuditReportsTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: any,
    ) => {
      const paginate = pagination?.current
        ? {
            pageNumber: pagination?.current || 1,
            pageSize: pagination?.pageSize || 20,
          }
        : {};
      const facilityNameFilter = filters?.facilityName
        ? {
            facilityName: filters?.facilityName?.[0] as string,
          }
        : {};
      const statusFilter = filters?.status
        ? {
            status: filters?.status as string[],
          }
        : {};
      const sortDirection =
        sorter?.field && sorter?.order
          ? {
              orderBy: sorter?.field === 'auditEndDate' ? 'AuditEndDate' : 'SubmittedDate',
              direction: sorter?.order === 'ascend' ? 'Asc' : 'Desc',
            }
          : {};

      setAuditReportsParams({
        ...paginate,
        ...facilityNameFilter,
        ...statusFilter,
        ...sortDirection,
        bapNumber: initParams?.bapNumber !== null ? initParams?.bapNumber : 'xyz',
      });
    },
    [initParams],
  );

  return {
    auditReports,
    isAuditReportsLoading,
    auditReportsParams,
    setAuditReportsParams,
    totalItems,
    onAuditReportsTableChange,
    filters: [facilityNameSearchFilter, bapNumberSearchFilter],
  };
};

export default useAuditReportsFilters;
